import { useState, useEffect, useRef } from "react";
import { Images, Plus, X, Trash, UploadSimple, TextT, FolderSimple } from "@phosphor-icons/react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminGallery() {
  const { admin } = useAdminAuth();
  const canDelete = admin?.allow_delete !== false;

  const [tab, setTab] = useState("albums"); // 'albums' | 'images'
  const [albums, setAlbums] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Image drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef();
  const [form, setForm] = useState({ title: "", file: null, album_id: "" });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  // Album drawer
  const [albumDrawerOpen, setAlbumDrawerOpen] = useState(false);
  const [albumSubmitting, setAlbumSubmitting] = useState(false);
  const albumFileRef = useRef();
  const [albumForm, setAlbumForm] = useState({ name: "", file: null });
  const [albumPreview, setAlbumPreview] = useState(null);
  const [albumError, setAlbumError] = useState("");
  const [deletingAlbumId, setDeletingAlbumId] = useState(null);

  // ── Fetchers ────────────────────────────────────────────────────────────────
  const fetchAlbums = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums`);
      const data = await res.json();
      setAlbums(Array.isArray(data) ? data : []);
    } catch { setAlbums([]); }
  };

  const fetchItems = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/gallery`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
  };

  useEffect(() => {
    Promise.all([fetchAlbums(), fetchItems()]).finally(() => setLoading(false));
  }, []);

  // ── Image handlers ──────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((f) => ({ ...f, file }));
    setPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm({ title: "", file: null, album_id: "" });
    setPreview(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.file) { setError("Please upload an image."); return; }
    if (!form.title.trim()) { setError("Please enter a title."); return; }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("file", form.file);
      if (form.album_id) fd.append("album_id", form.album_id);
      const res = await fetch(`${BACKEND_URL}/api/gallery`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      await Promise.all([fetchItems(), fetchAlbums()]);
      resetForm();
      setDrawerOpen(false);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    try {
      await fetch(`${BACKEND_URL}/api/gallery/${id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((x) => x.id !== id));
      await fetchAlbums();
    } finally {
      setDeleteId(null);
    }
  };

  // ── Album handlers ──────────────────────────────────────────────────────────
  const handleAlbumFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAlbumForm((f) => ({ ...f, file }));
    setAlbumPreview(URL.createObjectURL(file));
  };

  const resetAlbumForm = () => {
    setAlbumForm({ name: "", file: null });
    setAlbumPreview(null);
    setAlbumError("");
    if (albumFileRef.current) albumFileRef.current.value = "";
  };

  const handleAlbumSubmit = async (e) => {
    e.preventDefault();
    if (!albumForm.name.trim()) { setAlbumError("Please enter an album name."); return; }
    if (!albumForm.file) { setAlbumError("Please upload a cover image."); return; }
    setAlbumError("");
    setAlbumSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", albumForm.name);
      fd.append("file", albumForm.file);
      const res = await fetch(`${BACKEND_URL}/api/albums`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      await fetchAlbums();
      resetAlbumForm();
      setAlbumDrawerOpen(false);
    } catch {
      setAlbumError("Something went wrong. Please try again.");
    } finally {
      setAlbumSubmitting(false);
    }
  };

  const handleDeleteAlbum = async (id) => {
    if (!confirm("Delete this album and all images inside it?")) return;
    setDeletingAlbumId(id);
    try {
      await fetch(`${BACKEND_URL}/api/albums/${id}`, { method: "DELETE" });
      setAlbums((prev) => prev.filter((a) => a.id !== id));
      await fetchItems();
    } finally {
      setDeletingAlbumId(null);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Gallery</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {albums.length} album{albums.length !== 1 ? "s" : ""} · {items.length} image{items.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { resetAlbumForm(); setAlbumDrawerOpen(true); }}
            data-testid="add-album-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors"
            style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.25)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.06)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <FolderSimple size={16} weight="duotone" />
            New Album
          </button>
          <button
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            data-testid="add-image-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
          >
            <Plus size={16} weight="bold" />
            Add Gallery
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b" style={{ borderColor: "rgba(26,92,55,0.12)" }}>
        {[
          { key: "albums", label: `Albums (${albums.length})` },
          { key: "images", label: `Images (${items.length})` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className="px-4 py-2.5 text-sm font-medium transition-colors"
            style={{
              color: tab === t.key ? "#1A5C37" : "#4A5450",
              borderBottom: tab === t.key ? "2px solid #1A5C37" : "2px solid transparent",
              marginBottom: "-1px",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : tab === "albums" ? (
        albums.length === 0 ? (
          <EmptyState icon={FolderSimple} title="No albums yet" sub="Click &quot;New Album&quot; to create your first one." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {albums.map((a) => (
              <div
                key={a.id}
                className="relative rounded-xl overflow-hidden border group"
                style={{ borderColor: "rgba(26,92,55,0.1)" }}
              >
                <img src={`${BACKEND_URL}${a.cover_url}`} alt={a.name} className="w-full h-40 object-cover" />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-start justify-end p-3"
                  style={{ background: "linear-gradient(to top, rgba(26,33,31,0.7) 0%, transparent 60%)" }}
                >
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteAlbum(a.id)}
                      disabled={deletingAlbumId === a.id}
                      data-testid={`delete-album-${a.id}`}
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "rgba(184,92,56,0.9)", color: "#fff" }}
                    >
                      <Trash size={14} weight="duotone" />
                    </button>
                  )}
                </div>
                <div className="px-3 py-2 flex items-center justify-between" style={{ backgroundColor: "#fff" }}>
                  <p className="text-xs font-medium truncate" style={{ color: "#1C211F" }}>{a.name}</p>
                  <span className="text-xs ml-2 flex-shrink-0" style={{ color: "#4A5450" }}>{a.image_count} img</span>
                </div>
              </div>
            ))}
          </div>
        )
      ) : items.length === 0 ? (
        <EmptyState icon={Images} title="No images yet" sub="Click &quot;Add Gallery&quot; to upload your first image." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => {
            const album = albums.find((a) => a.id === item.album_id);
            return (
              <div
                key={item.id}
                className="relative rounded-xl overflow-hidden border group"
                style={{ borderColor: "rgba(26,92,55,0.1)" }}
              >
                <img src={`${BACKEND_URL}${item.image_url}`} alt={item.title} className="w-full h-40 object-cover" />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-3"
                  style={{ background: "linear-gradient(to top, rgba(26,33,31,0.7) 0%, transparent 50%)" }}
                >
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      disabled={deleteId === item.id}
                      className="self-end w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "rgba(184,92,56,0.9)", color: "#fff" }}
                    >
                      <Trash size={14} weight="duotone" />
                    </button>
                  )}
                  <p className="text-xs font-medium truncate" style={{ color: "#F7F5F0" }}>{item.title}</p>
                </div>
                <div className="px-3 py-2" style={{ backgroundColor: "#fff" }}>
                  <p className="text-xs font-medium truncate" style={{ color: "#1C211F" }}>{item.title}</p>
                  <p className="text-[10px] mt-0.5 truncate" style={{ color: "#4A5450" }}>
                    {album ? album.name : "Unassigned"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Image Drawer ──────────────────────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.35)" }} onClick={() => setDrawerOpen(false)} />
      )}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "min(420px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>Add Gallery</h3>
          <button onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg" style={{ color: "#4A5450" }}>
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Upload Image</label>
            <div
              className="relative flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer"
              style={{ borderColor: preview ? "#1A5C37" : "rgba(26,92,55,0.25)", backgroundColor: "#F7F5F0", minHeight: 160 }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              {preview ? (
                <img src={preview} alt="preview" className="max-h-36 rounded-lg object-cover" />
              ) : (
                <>
                  <UploadSimple size={28} weight="duotone" style={{ color: "#1A5C37" }} />
                  <p className="text-sm" style={{ color: "#4A5450" }}>Click to upload image</p>
                </>
              )}
              {preview && (
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1 rounded-full"
                  style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}
                  onClick={(e) => { e.stopPropagation(); setPreview(null); setForm((f) => ({ ...f, file: null })); if (fileRef.current) fileRef.current.value = ""; }}
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Title</label>
            <div className="relative">
              <TextT size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }} />
              <input
                required
                type="text"
                placeholder="Image title or caption"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className={inputCls}
                style={{ ...inputStyle, paddingLeft: "2.25rem" }}
              />
            </div>
          </div>

          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Album</label>
            <select
              data-testid="select-album"
              value={form.album_id}
              onChange={(e) => setForm((f) => ({ ...f, album_id: e.target.value }))}
              className={inputCls}
              style={inputStyle}
            >
              <option value="">— No album —</option>
              {albums.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {error && <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>{error}</p>}
        </form>
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button type="button" onClick={() => setDrawerOpen(false)} className="flex-1 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0" }}>
            {submitting ? "Uploading..." : "Upload Image"}
          </button>
        </div>
      </div>

      {/* ── Album Drawer ──────────────────────────────────────────────────── */}
      {albumDrawerOpen && (
        <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.35)" }} onClick={() => setAlbumDrawerOpen(false)} />
      )}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${albumDrawerOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "min(420px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>New Album</h3>
          <button onClick={() => setAlbumDrawerOpen(false)} className="p-1.5 rounded-lg" style={{ color: "#4A5450" }}>
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleAlbumSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Cover Image</label>
            <div
              className="relative flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer"
              style={{ borderColor: albumPreview ? "#1A5C37" : "rgba(26,92,55,0.25)", backgroundColor: "#F7F5F0", minHeight: 160 }}
              onClick={() => albumFileRef.current?.click()}
            >
              <input ref={albumFileRef} type="file" accept="image/*" className="hidden" onChange={handleAlbumFileChange} />
              {albumPreview ? (
                <img src={albumPreview} alt="preview" className="max-h-36 rounded-lg object-cover" />
              ) : (
                <>
                  <UploadSimple size={28} weight="duotone" style={{ color: "#1A5C37" }} />
                  <p className="text-sm" style={{ color: "#4A5450" }}>Click to upload cover</p>
                </>
              )}
              {albumPreview && (
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1 rounded-full"
                  style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}
                  onClick={(e) => { e.stopPropagation(); setAlbumPreview(null); setAlbumForm((f) => ({ ...f, file: null })); if (albumFileRef.current) albumFileRef.current.value = ""; }}
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Album Name</label>
            <input
              required
              type="text"
              placeholder="e.g. Bellandur Lake Cleanup 2025"
              value={albumForm.name}
              onChange={(e) => setAlbumForm((f) => ({ ...f, name: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              data-testid="album-name-input"
            />
          </div>

          {albumError && <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>{albumError}</p>}
        </form>
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button type="button" onClick={() => setAlbumDrawerOpen(false)} className="flex-1 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}>Cancel</button>
          <button onClick={handleAlbumSubmit} disabled={albumSubmitting} className="flex-1 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: albumSubmitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0" }} data-testid="album-submit-btn">
            {albumSubmitting ? "Creating..." : "Create Album"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
        <Icon size={28} weight="duotone" style={{ color: "#1A5C37" }} />
      </div>
      <p className="text-sm font-medium" style={{ color: "#1C211F" }}>{title}</p>
      <p className="text-xs mt-1" style={{ color: "#4A5450" }} dangerouslySetInnerHTML={{ __html: sub }} />
    </div>
  );
}
